import * as React from 'react';
import { Link } from 'react-router';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { cn } from '../lib/cn';
import { Card } from '../primitives/Card';
// product/ -> charts/ is the sanctioned direction. charts/ is a LEAF and never
// imports back out, which is what keeps this from being a cycle — see
// charts/index.ts. Imported by file path, not through the charts barrel, so
// the two index files stay independent.
import { ChartLine } from '../charts/ChartLine';

/**
 * StatCard — the ONE dashboard metric tile.
 *
 * Phase 5 / ADR_SHARED_COMPONENT_DOCTRINE_2026-08-03: three implementations of
 * this existed — StatCard (client portal), KpiCard (AdminDashboard) and
 * MetricTile (SalesDashboard) — which is why the three dashboards read as three
 * different products. KpiCard and MetricTile are deleted; their props (`icon`,
 * `tone`, `link`, `loading`) were folded in here.
 *
 * Every prop beyond `label`/`value` is optional, so the original callers are
 * unaffected.
 */

export type StatCardTrend = 'up' | 'down' | 'flat';
export type StatCardTone =
  | 'default' | 'success' | 'primary' | 'warning' | 'danger' | 'info' | 'muted';

interface StatCardProps {
  label: string;
  value: React.ReactNode; // pre-formatted
  delta?: string;
  trend?: StatCardTrend;
  sub?: React.ReactNode;
  /** Lucide icon component (NOT an element) — rendered top-right, tone-coloured. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Drives the icon colour, and the value colour for warning/danger/muted. */
  tone?: StatCardTone;
  /** Footer link, e.g. { to: '/admin/customers', label: 'Open customers' }. */
  link?: { to: string; label: string };
  /** Skeleton in the final layout, so nothing reflows when data lands. */
  loading?: boolean;
  /** Hover-lift. Defaults true (the original StatCard behaviour); operational
   *  tiles that are not themselves clickable should pass false. */
  interactive?: boolean;
  /** `compact` for dense strips (Phase 7's /admin KPI row). */
  size?: 'default' | 'compact';
  /**
   * `'now'` marks the tile as a STOCK metric that does NOT obey a period filter.
   *
   * "Open tickets in June" is not a real number — there is only "open tickets
   * now", or "tickets opened in June", which is a different question. When a
   * screen offers a period filter, every tile it does not scope must say so, or
   * the user acts on a belief the interface contradicts (REQUIREMENTS F-2).
   *
   * Render it only while a non-current period is selected; permanently on, it
   * is noise.
   */
  scope?: 'now';
  /**
   * Recent history for this metric, oldest first — rendered as an axis-less
   * sparkline beside the value.
   *
   * SHAPE, not measurement: it answers "which way is this going", and the value
   * above answers "how much". Give it a `sparklineLabel` describing the trend in
   * words, because a bare polyline is nothing to a screen reader.
   *
   * Fewer than two points renders nothing — a one-point trend is not a trend.
   */
  sparkline?: number[];
  sparklineLabel?: string;
  className?: string;
}

const TREND_COLOR: Record<StatCardTrend, string> = {
  up: 'text-success',
  down: 'text-danger',
  flat: 'text-muted-foreground',
};

const TREND_ICON: Record<StatCardTrend, React.ComponentType<{ className?: string }>> = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  flat: Minus,
};

const TONE: Record<StatCardTone, { icon: string; value: string }> = {
  default: { icon: 'text-muted-foreground', value: 'text-foreground' },
  success: { icon: 'text-success', value: 'text-foreground' },
  primary: { icon: 'text-primary', value: 'text-foreground' },
  info: { icon: 'text-info', value: 'text-foreground' },
  warning: { icon: 'text-warning', value: 'text-warning' },
  danger: { icon: 'text-danger', value: 'text-danger' },
  muted: { icon: 'text-muted-foreground', value: 'text-muted-foreground' },
};

function StatCard({
  label,
  value,
  delta,
  trend,
  sub,
  icon: Icon,
  tone = 'default',
  link,
  loading = false,
  interactive = true,
  size = 'default',
  scope,
  sparkline,
  sparklineLabel,
  className,
}: StatCardProps) {
  const TrendIcon = trend ? TREND_ICON[trend] : null;
  const t = TONE[tone];
  const compact = size === 'compact';
  // A single point is not a trend, and a compact tile has no room for one.
  const showSpark = !!sparkline && sparkline.length > 1 && !compact && !loading;

  return (
    <Card interactive={interactive} className={cn(compact ? 'p-3' : 'p-5', className)}>
      <div className="flex items-start justify-between gap-3">
        <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs font-medium text-muted-foreground">
          {label}
          {/* Beside the LABEL, not top-right: that slot is already contested by
              `delta`/`icon`. It also reads correctly here — it qualifies what
              the number counts. Bordered, not filled: a filled pill risks
              failing AA against bg-muted. Real text, never aria-hidden. */}
          {scope === 'now' && (
            <span className="inline-flex shrink-0 items-center rounded-full border border-border px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Now
            </span>
          )}
        </span>
        {delta && trend && !loading && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums',
              TREND_COLOR[trend]
            )}
          >
            {TrendIcon && <TrendIcon className="size-3" aria-hidden />}
            {delta}
          </span>
        )}
        {Icon && !delta && (
          <span className={cn('inline-flex shrink-0 items-center justify-center', t.icon)}>
            <Icon className={compact ? 'size-4' : 'size-5'} aria-hidden />
          </span>
        )}
      </div>

      {loading ? (
        // Skeleton occupies the same box as the real value — no reflow.
        <div
          className={cn(
            'mt-2 animate-pulse rounded-sm bg-muted',
            compact ? 'h-6 w-20' : 'h-8 w-28'
          )}
          aria-hidden
        />
      ) : (
        <div className="mt-2 flex items-end justify-between gap-3">
          <div
            className={cn(
              'font-display font-bold leading-none tabular-nums',
              compact ? 'text-xl' : 'text-3xl',
              t.value
            )}
          >
            {value}
          </div>
          {showSpark && sparkline && (
            <ChartLine
              variant="spark"
              labelStrategy="none"
              ariaLabel={sparklineLabel ?? `${label} over recent periods.`}
              data={sparkline.map((v, i) => ({ label: String(i), value: v }))}
            />
          )}
        </div>
      )}

      {sub && !loading && (
        <div className="mt-1.5 text-xs text-muted-foreground">{sub}</div>
      )}

      {link && !loading && (
        <Link
          to={link.to}
          className="mt-3 inline-flex items-center text-xs font-medium text-primary hover:underline"
        >
          {link.label} →
        </Link>
      )}
    </Card>
  );
}

export { StatCard };
export type { StatCardProps };
